import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { sendMainPromotionLinkClick } from 'src/utils/marketing/enhancedEcommerce';
import styles from './HeaderPromotionLine.styles';
import { sendBannerButtonClick, sendBannerClosed, sendBannerDisplayed } from './analytics';
import { PromotionLineData } from './useHeaderPromotionLogic';

export interface HeaderPromotionLineProps {
  promotionLineData: PromotionLineData;
  onClose(): void;
}

const HeaderPromotionLine = (props: HeaderPromotionLineProps) => {
  const { promotionLineData, onClose } = props;
  const { content, link, linkLabel, iconUrl, withDetailsLink } = promotionLineData;
  const { pathname } = useRouter();
  const pageSlugWithoutLang = pathname.split('/').filter(i => i !== '[lang]').join('/');

  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const spanElem = spanRef.current;
    if (!spanElem) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (link && spanElem.contains(link)) {
        const spanInnerLinkHref = link.href;
        sendMainPromotionLinkClick(spanInnerLinkHref);
      }
    };

    spanElem.addEventListener('click', handleClick);

    return () => {
      spanElem.removeEventListener('click', handleClick);
    };
  }, [content]);

  useEffect(() => {
    sendBannerDisplayed(pageSlugWithoutLang);
  }, [pageSlugWithoutLang]);

  const handleClose = () => {
    onClose();
    sendBannerClosed(pageSlugWithoutLang);
  };

  const handleLinkClick = () => {
    sendBannerButtonClick(pageSlugWithoutLang);
  };

  return (
    <div className="HeaderPromotionLine" data-marker={'Header Promotion Line'}>
      <div className="HeaderPromotionLine__container">
        <div className="HeaderPromotionLine__content">
          {iconUrl && (
            <img
              src={iconUrl}
              alt="Offer icon"
              className='HeaderPromotionLine__icon'
            />
          )}

          <span
            ref={spanRef}
            className='HeaderPromotionLine__text'
            dangerouslySetInnerHTML={{ __html: content }}
          />

          {withDetailsLink && (
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              onClick={handleLinkClick}
              className="HeaderPromotionLine__link"
            >
              {linkLabel}
            </a>
          )}
        </div>

        <button
          className="HeaderPromotionLine__buttonClose"
          onClick={handleClose}
          aria-label="button close"
          data-marker="Close Header Promotion"
        >
          <i className="icon-close" />
        </button>
      </div>

      <style jsx>{styles}</style>
    </div>
  );
};

export default HeaderPromotionLine;
